export const normalizeString = (str = "") => {
  return str
    .toLowerCase()
    .replace(/\s+/g, "") // Remove all spaces
    .replace(/[-._]/g, "") // Remove dashes, dots, underscores
    .replace(/[^a-z0-9]/gi, ""); // Remove other special characters
};

/**
 * Calculate Levenshtein distance (edit distance) between two strings
 * Lower distance = better match
 * Used for typo-tolerant searching
 */
const levenshteinDistance = (str1, str2) => {
  const matrix = [];

  for (let i = 0; i <= str2.length; i++) {
    matrix[i] = [i];
  }

  for (let j = 0; j <= str1.length; j++) {
    matrix[0][j] = j;
  }

  for (let i = 1; i <= str2.length; i++) {
    for (let j = 1; j <= str1.length; j++) {
      if (str2.charAt(i - 1) === str1.charAt(j - 1)) {
        matrix[i][j] = matrix[i - 1][j - 1];
      } else {
        matrix[i][j] = Math.min(
          matrix[i - 1][j - 1] + 1, // substitution
          matrix[i][j - 1] + 1, // insertion
          matrix[i - 1][j] + 1, // deletion
        );
      }
    }
  }

  return matrix[str2.length][str1.length];
};

/**
 * Calculate match score (0-100)
 * Higher score = better match
 */
const calculateMatchScore = (query, target) => {
  const normalizedQuery = normalizeString(query);
  const normalizedTarget = normalizeString(target);

  // Exact match (after normalization)
  if (normalizedTarget === normalizedQuery) {
    return 100;
  }

  // Contains match (substring)
  if (normalizedTarget.includes(normalizedQuery)) {
    return 90;
  }

  // Starts with match
  if (normalizedTarget.startsWith(normalizedQuery)) {
    return 85;
  }

  // Fuzzy match using Levenshtein distance
  const distance = levenshteinDistance(normalizedQuery, normalizedTarget);
  const maxLen = Math.max(normalizedQuery.length, normalizedTarget.length);

  // Score based on distance (inversely proportional)
  if (distance === 0) return 100;
  if (distance <= 2) return 80 - distance * 10;
  if (distance <= 5) return 60 - distance * 5;
  if (distance <= maxLen / 2) return 30;

  return 0;
};

/**
 * Search product varieties with frontend fuzzy matching
 * Optimized for special characters like w-180, w - 180, etc.
 */
export const searchProductVarieties = (query, varieties) => {
  if (!query || !query.trim()) {
    return [];
  }

  const trimmedQuery = query.trim();
  const normalizedQuery = normalizeString(trimmedQuery);

  // Score each variety
  const scored = varieties
    .map((variety) => {
      let score = 0;

      // Search in variety name
      const nameScore = calculateMatchScore(trimmedQuery, variety.varietyName);
      score = Math.max(score, nameScore);

      // Search in grade
      if (variety.grade) {
        const gradeScore = calculateMatchScore(trimmedQuery, variety.grade);
        score = Math.max(score, gradeScore);
      }

      // Search in product name (if populated)
      if (variety.productId?.name) {
        const productScore = calculateMatchScore(
          trimmedQuery,
          variety.productId.name,
        );
        score = Math.max(score, productScore * 0.8); // Lower weight for product name
      }

      return { variety, score };
    })
    .filter((item) => item.score > 0) // Only keep matches
    .sort((a, b) => b.score - a.score); // Sort by score (descending)

  return scored.map((item) => item.variety);
};

/**
 * Group search results by category for display
 */
export const groupResultsByCategory = (varieties) => {
  const grouped = {};

  varieties.forEach((variety) => {
    const categoryName =
      variety.productId?.subCategoryId?.categoryId?.name || "Other";

    if (!grouped[categoryName]) {
      grouped[categoryName] = [];
    }
    grouped[categoryName].push(variety);
  });

  return grouped;
};

/**
 * Format search result for display
 */
export const formatSearchResult = (variety) => {
  return {
    id: variety._id,
    name: variety.varietyName,
    grade: variety.grade || "N/A",
    productName: variety.productId?.name || "Unknown",
    categoryId: variety.productId?.subCategoryId?.categoryId?._id,
    subCategoryId: variety.productId?.subCategoryId?._id,
    productId: variety.productId?._id,
    img: variety.img,
    supplierCount: variety.supplierCount || 0,
  };
};

/**
 * Highlight matching parts in search result
 * Returns HTML-safe highlighted text
 */
export const highlightMatch = (text, query) => {
  if (!query) return text;

  const normalizedText = normalizeString(text);
  const normalizedQuery = normalizeString(query);

  if (normalizedText.includes(normalizedQuery)) {
    // Find the original substring position (approximate)
    const regex = new RegExp(`(${query})`, "gi");
    return text.replace(regex, "<mark>$1</mark>");
  }

  return text;
};
